/**
 * Accessibility Utilities
 * ARIA labels, keyboard navigation, screen reader support
 */

// ARIA label helpers
export const aria = {
  // Button labels
  closeButton: { 'aria-label': 'Close', role: 'button' },
  menuButton: { 'aria-label': 'Open menu', 'aria-haspopup': 'true' },
  searchButton: { 'aria-label': 'Search', role: 'button' },
  deleteButton: { 'aria-label': 'Delete', role: 'button' },
  editButton: { 'aria-label': 'Edit', role: 'button' },
  
  // Navigation
  navigation: { role: 'navigation', 'aria-label': 'Main navigation' },
  breadcrumb: { role: 'navigation', 'aria-label': 'Breadcrumb' },
  
  // Sections
  main: { role: 'main', 'aria-label': 'Main content' },
  complementary: { role: 'complementary', 'aria-label': 'Sidebar' },
  search: { role: 'search', 'aria-label': 'Search' },
  
  // Interactive elements
  dialog: (label) => ({ role: 'dialog', 'aria-labelledby': label, 'aria-modal': 'true' }),
  alert: (label) => ({ role: 'alert', 'aria-live': 'assertive', 'aria-label': label }),
  status: (label) => ({ role: 'status', 'aria-live': 'polite', 'aria-label': label }),
  
  // Form elements
  required: { 'aria-required': 'true' },
  invalid: (message) => ({ 'aria-invalid': 'true', 'aria-errormessage': message }),
  describedBy: (id) => ({ 'aria-describedby': id }),
  labelledBy: (id) => ({ 'aria-labelledby': id }),
  
  // Expanded/collapsed states
  expanded: (isExpanded) => ({ 'aria-expanded': isExpanded ? 'true' : 'false' }),
  pressed: (isPressed) => ({ 'aria-pressed': isPressed ? 'true' : 'false' }),
  selected: (isSelected) => ({ 'aria-selected': isSelected ? 'true' : 'false' }),
  checked: (isChecked) => ({ 'aria-checked': isChecked ? 'true' : 'false' }),
  
  // Loading states
  loading: { 'aria-busy': 'true', 'aria-live': 'polite' },
  loaded: { 'aria-busy': 'false' },
  
  // Hidden elements
  hidden: { 'aria-hidden': 'true' },
  visible: { 'aria-hidden': 'false' },
};

// Keyboard navigation helpers
export const keyboard = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
  
  // Check if key matches
  is: (event, key) => event.key === key,
  
  // Common handlers
  handleEnterSpace: (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback(event);
    }
  },
  
  handleEscape: (event, callback) => {
    if (event.key === 'Escape') {
      callback(event);
    }
  },
  
  handleArrows: (event, callbacks) => {
    const { onUp, onDown, onLeft, onRight } = callbacks;
    
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        onUp && onUp(event);
        break;
      case 'ArrowDown':
        event.preventDefault();
        onDown && onDown(event);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        onLeft && onLeft(event);
        break;
      case 'ArrowRight':
        event.preventDefault();
        onRight && onRight(event);
        break;
      default:
        break;
    }
  },
};

// Focus management
export const focus = {
  // Trap focus within an element
  trapFocus: (element) => {
    const focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };
    
    element.addEventListener('keydown', handleTabKey);
    
    // Focus first element
    firstElement && firstElement.focus();
    
    // Return cleanup function
    return () => element.removeEventListener('keydown', handleTabKey);
  },
  
  // Set focus to element
  setFocus: (element) => {
    element && element.focus();
  },
  
  // Restore focus to previous element
  saveFocus: () => {
    return document.activeElement;
  },
  
  restoreFocus: (element) => {
    element && element.focus();
  },
};

// Screen reader utilities
export const screenReader = {
  // Announce to screen reader
  announce: (message, priority = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  },
  
  // Screen reader only text
  srOnly: 'absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0',
  srOnlyFocusable: 'absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0 focus:not-sr-only',
};

// Skip navigation link
export const skipLink = {
  className: 'sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:p-4 focus:bg-blue-600 focus:text-white',
  href: '#main-content',
  text: 'Skip to main content',
};

// Color contrast helpers
export const contrast = {
  // Check if color has sufficient contrast
  checkContrast: (foreground, background) => {
    // Simplified - in production, use a proper contrast checker
    return true;
  },
  
  // Accessible color combinations
  combinations: {
    text: {
      light: 'text-gray-900',
      dark: 'text-gray-100',
    },
    background: {
      light: 'bg-white',
      dark: 'bg-gray-900',
    },
  },
};

// Accessible form helpers
export const form = {
  // Generate unique IDs for labels
  generateId: (prefix = 'field') => `${prefix}-${Math.random().toString(36).substr(2, 9)}`,
  
  // Form field wrapper with proper labeling
  fieldProps: (id, label, error) => ({
    id,
    'aria-label': label,
    'aria-invalid': error ? 'true' : 'false',
    'aria-describedby': error ? `${id}-error` : undefined,
  }),
  
  // Error message props
  errorProps: (id) => ({
    id: `${id}-error`,
    role: 'alert',
    'aria-live': 'assertive',
  }),
  
  // Help text props
  helpProps: (id) => ({
    id: `${id}-help`,
    'aria-live': 'polite',
  }),
};

// Mobile touch accessibility
export const touch = {
  // Minimum touch target size (44x44px recommended)
  minTouchTarget: 'min-h-[44px] min-w-[44px]',
  
  // Touch-friendly spacing
  spacing: 'p-3',
  
  // Disable text selection for interactive elements
  noSelect: 'select-none',
  
  // Touch feedback
  touchFeedback: 'active:bg-gray-100 transition-colors duration-150',
};

// Reduced motion support
export const motion = {
  // Check if user prefers reduced motion
  prefersReducedMotion: () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },
  
  // Conditional animation class
  animate: (animationClass) => {
    return motion.prefersReducedMotion() ? '' : animationClass;
  },
  
  // Reduced motion class
  respectMotion: 'motion-reduce:transition-none motion-reduce:animate-none',
};

// Generate accessible component props
export const makeAccessible = {
  button: (label, options = {}) => ({
    type: options.type || 'button',
    'aria-label': label,
    tabIndex: options.disabled ? -1 : 0,
    disabled: options.disabled,
    'aria-disabled': options.disabled ? 'true' : 'false',
    ...aria.expanded(options.expanded),
    ...aria.pressed(options.pressed),
  }),
  
  link: (label, href, options = {}) => ({
    href,
    'aria-label': label,
    target: options.external ? '_blank' : undefined,
    rel: options.external ? 'noopener noreferrer' : undefined,
    'aria-current': options.current ? 'page' : undefined,
  }),
  
  input: (label, type = 'text', options = {}) => ({
    type,
    'aria-label': label,
    'aria-required': options.required ? 'true' : 'false',
    'aria-invalid': options.error ? 'true' : 'false',
    'aria-describedby': options.helpId || (options.error ? `${options.id}-error` : undefined),
  }),
  
  modal: (title, options = {}) => ({
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': options.titleId || 'modal-title',
    tabIndex: -1,
  }),
};

export default {
  aria,
  keyboard,
  focus,
  screenReader,
  skipLink,
  contrast,
  form,
  touch,
  motion,
  makeAccessible,
};
